import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaSearch } from "react-icons/fa";
import { RiTwitterXLine } from "react-icons/ri";
import { MdAccessibilityNew } from "react-icons/md";
import { FaChevronDown } from "react-icons/fa6";
import AccessibilityDrawer from "./AccessibilityDrawer";
import { useTranslation } from "react-i18next";
import Translate from "./Translate";
import ExternalLinkOpener from "./ExternalLinkOpener";

export default function TopBar({ data, onSkipToMain }) {
  const { i18n } = useTranslation();

  const [isChanging, setIsChanging] = useState(false);
  const [targetLangName, setTargetLangName] = useState("");
  const [pendingLang, setPendingLang] = useState(null);
  const [showLangConfirm, setShowLangConfirm] = useState(false);
  const confirmCancelRef = useRef(null);

  const [langOpen, setLangOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const langDropdownRef = useRef(null);
  const langTriggerRef = useRef(null);

  // Step 1: User picks a language — show advisory dialog BEFORE changing
  const handleLangSelect = (lang) => {
    if (lang === i18n.language) {
      setLangOpen(false);
      return;
    }
    setPendingLang(lang);
    setTargetLangName(lang === "en" ? "English" : "हिंदी");
    setLangOpen(false);
    setShowLangConfirm(true); // Advise user BEFORE any context change
  };

  // Step 2: User confirms — now apply the change
  const confirmLangChange = () => {
    setShowLangConfirm(false);
    setIsChanging(true);
    i18n.changeLanguage(pendingLang);
    localStorage.setItem("appLang", pendingLang);
    setTimeout(() => {
      window.location.reload();
    }, 1200);
  };

  // Step 2 (cancel): User cancels — no change occurs
  const cancelLangChange = () => {
    setShowLangConfirm(false);
    setPendingLang(null);
  };

  // Trap focus to Cancel button when dialog opens (keyboard accessibility)
  useEffect(() => {
    if (showLangConfirm && confirmCancelRef.current) {
      confirmCancelRef.current.focus();
    }
  }, [showLangConfirm]);

  // Close Language Dropdown on Click Outside or Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangOpen(false);
      }
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (langOpen) {
          setLangOpen(false);
          langTriggerRef.current?.focus();
        }
        setSearchOpen(false);
      }
    };

    if (langOpen || searchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [langOpen, searchOpen]);

  const handleLangKeyDown = (e) => {
    if (!langOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setLangOpen(true);
      }
      return;
    }

    const menuItems = langDropdownRef.current?.querySelectorAll('[role="menuitem"]');
    if (!menuItems?.length) return;

    const currentIndex = Array.from(menuItems).indexOf(document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % menuItems.length;
      menuItems[nextIndex].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
      menuItems[prevIndex].focus();
    } else if (e.key === 'Tab') {
      setLangOpen(false);
    }
  };


  const handleSearchClick = () => {
    setSearchOpen((prev) => !prev);
    setTimeout(() => {
      if (searchInputRef.current) searchInputRef.current.focus();
    }, 100);
  };

  return (
    <>
      {/* WCAG 3.2.2 – Advisory dialog: advise user BEFORE context change */}
      {showLangConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="lang-confirm-title"
          aria-describedby="lang-confirm-desc"
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50"
          onKeyDown={(e) => { if (e.key === 'Escape') cancelLangChange(); }}
        >
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-sm w-full mx-4">
            <h2 id="lang-confirm-title" className="text-base font-semibold text-primary-800 mb-2">
              {targetLangName === "हिंदी" ? "भाषा बदलें?" : "Change Language?"}
            </h2>
            <p id="lang-confirm-desc" className="text-sm text-gray-600 mb-5">
              {targetLangName === "हिंदी"
                ? `भाषा को "${targetLangName}" में बदलने से पूरा पृष्ठ पुनः लोड होगा और सामग्री बदल जाएगी।`
                : `Switching to "${targetLangName}" will reload the page and change all content. Do you want to continue?`}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                ref={confirmCancelRef}
                onClick={cancelLangChange}
                className="px-4 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {targetLangName === "हिंदी" ? "रद्द करें" : "Cancel"}
              </button>
              <button
                onClick={confirmLangChange}
                className="px-4 py-1.5 text-sm rounded bg-primary-700 text-white hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {targetLangName === "हिंदी" ? "ठीक है" : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading banner shown AFTER user confirmed */}
      {isChanging && (
        <div className="fixed top-0 left-0 w-full bg-primary-700 text-white py-2.5 z-[9999] shadow-2xl flex items-center justify-center space-x-3 transition-all animate-slide-down">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span className="font-medium text-sm sm:text-base">
            {targetLangName === "हिंदी" ? "भाषा बदली जा रही है: हिंदी..." : "Changing language to English..."}
          </span>
        </div>
      )}
      <div className="w-full border-b border-primary-100 no-print">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center px-1 sm:px-4 py-0.5 sm:py-1 gap-y-1 text-xs sm:text-sm">
          {/* Left Side */}
          <div className="flex items-center space-x-4 text-primary-700">
            <button
              onClick={onSkipToMain}
              className="hover:underline text-sm font-medium cursor-pointer focus:ring-2 focus:ring-primary-500 focus:outline-none rounded px-1"
            >
              <Translate text="skip-to-main-content" />
            </button>
            <span className="text-primary-700" aria-hidden="true">|</span>
            <Link to="/screen-reader-access" className="hover:underline text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:outline-none rounded px-1"><Translate text="screen_reader" /></Link>
          </div>
          {/* Right Side */}
          <div className="flex items-center space-x-1 xs:space-x-2 sm:space-x-3 text-primary-700 relative text-base flex-wrap justify-center sm:justify-end gap-y-2">
            <ExternalLinkOpener
              url={data.facebook_url}
              ariaLabel="Visit our Facebook page"
              className="p-1.5 sm:p-2 hover:bg-primary-50 rounded-full transition-colors flex items-center justify-center translate-y-[-2px] focus:ring-2 focus:ring-primary-500 focus:outline-none"
              text={<FaFacebookF className="w-3.5 h-3.5 xs:w-4 xs:h-4" aria-hidden="true" />}
            />
            <ExternalLinkOpener
              url={data.twitter_url}
              ariaLabel="Visit our X (Twitter) page"
              className="p-1.5 sm:p-2 hover:bg-primary-50 rounded-full transition-colors flex items-center justify-center translate-y-[-2px] focus:ring-2 focus:ring-primary-500 focus:outline-none"
              text={<RiTwitterXLine className="w-3.5 h-3.5 xs:w-4 xs:h-4" aria-hidden="true" />}
            />


            <span className="text-primary-700" aria-hidden="true">|</span>

            <div
              className="relative"
              ref={langDropdownRef}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setLangOpen(false);
                }
              }}
            >
              <button
                ref={langTriggerRef}
                className="flex items-center space-x-1 cursor-pointer p-1.5 hover:bg-primary-50 rounded transition-colors focus:ring-2 focus:ring-primary-500 focus:outline-none"
                onClick={() => setLangOpen((prev) => !prev)}
                onKeyDown={handleLangKeyDown}
                aria-expanded={langOpen}
                aria-haspopup="true"
                aria-label="Language Selector"
              >
                <span className="text-xs sm:text-sm">{i18n.language == 'en' ? "English" : "हिंदी"}</span>
                <FaChevronDown className="w-3 h-3" aria-hidden="true" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded shadow z-20 min-w-[80px]" role="menu">
                  <button
                    className={`w-full text-left px-3 py-1 hover:bg-primary-50 cursor-pointer focus:bg-primary-50 focus:outline-none ${i18n.language === "en" ? "font-semibold" : ""}`}
                    onClick={() => handleLangSelect("en")}
                    onKeyDown={handleLangKeyDown}
                    role="menuitem"
                  >
                    English
                  </button>
                  <button
                    className={`w-full text-left px-3 py-1 hover:bg-primary-50 cursor-pointer focus:bg-primary-50 focus:outline-none ${i18n.language === "hi" ? "font-semibold" : ""}`}
                    onClick={() => handleLangSelect("hi")}
                    onKeyDown={handleLangKeyDown}
                    role="menuitem"
                  >
                    हिंदी
                  </button>
                </div>
              )}
            </div>

            <span className="text-gray-600" aria-hidden="true">|</span>
            <button
              onClick={() => setAccessibilityOpen(true)}
              className="p-1.5 sm:p-2 hover:bg-primary-50 rounded-full focus:ring-2 focus:ring-primary-500 transition-colors"
              aria-label="Open accessibility options"
              aria-haspopup="dialog"
            >
              <MdAccessibilityNew className="w-5 h-5 text-primary-600" />
            </button>
            <span className="text-gray-600" aria-hidden="true">|</span>

            {/* Search Bar */}
            <div className="relative" ref={searchContainerRef}>
              <button
                onClick={handleSearchClick}
                className={`p-1.5 sm:p-2 rounded-full transition-all duration-300 flex items-center justify-center ${searchOpen ? 'bg-primary-100 text-primary-800' : 'hover:bg-primary-50 text-primary-700'}`}
                aria-label="Toggle search"
              >
                <FaSearch className="w-4 h-4 sm:w-5 h-5" />
              </button>

              {searchOpen && (
                <div className="absolute right-0 top-10 w-48 xs:w-56 sm:w-72 bg-white border border-primary-300 rounded-lg shadow-xl p-1.5 z-50 flex items-center animate-slide-down">
                  <div className="relative w-full">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Type and search..."
                      className="w-full pl-3 pr-10 py-1.5 bg-gray-50 border border-primary-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-xs sm:text-sm transition-all text-primary-900"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const query = e.target.value;
                          if (query) console.log("Search query:", query);
                        }
                      }}
                    />
                    <button
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-primary-600 hover:text-primary-800 transition-colors"
                      aria-label="Submit search"
                      onClick={() => {
                        const val = searchInputRef.current?.value;
                        if (val) console.log("Search query:", val);
                      }}
                    >
                      <FaSearch className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="ml-auto text-primary-700 text-xs sm:text-sm pl-3">
              <ExternalLinkOpener
                url="https://employmentnews.gov.in/empnewsnew/members"
                text={<Translate text="eversion" />}
                className="hover:underline focus:ring-2 focus:ring-primary-500 focus:outline-none rounded px-1"
                ariaLabel="Open e-version of Employment News in a new tab"
              />
            </div>
          </div>

        </div>

        {/* Accessibility Drawer */}
        <AccessibilityDrawer
          isOpen={accessibilityOpen}
          onClose={() => setAccessibilityOpen(false)}
        />
      </div>
    </>
  );
}
